import { useEffect, useState } from 'react';
import {
  logAchievementCelebrationView,
  markAchievementsRead,
  type AchievementRow,
} from '@busy/api';
import { ModalShell, useToast } from '@busy/ui';
import { useT } from '../lib/i18n';
import { ACHIEVEMENT_CATALOG, thresholdLabel, intensityForThreshold } from '../lib/achievements';
import { badgeArtworkUrl } from '../lib/badgeArtwork';
import { BadgeEffect } from './BadgeEffect';
import { renderBadgeImage } from '../lib/badgeImage';

// Full-screen celebration when the user crosses one or more milestones.
// Used by Today on mount when useUnreadAchievements returns rows. If multiple
// milestones were earned at once (e.g. a high-traffic shift crossing two
// revenue tiers + a cuts milestone), the modal advances through them one at a
// time with a Continue / Next button before closing + marking all as read.

export function CelebrationModal({
  achievements,
  onClose,
}: {
  achievements: AchievementRow[];
  onClose: () => void;
}) {
  const t = useT();
  const toast = useToast();
  const [idx, setIdx] = useState(0);
  const total = achievements.length;
  const ach = achievements[idx];

  const isLast = idx === total - 1;
  const catalog = ACHIEVEMENT_CATALOG[ach.kind];
  const thr = thresholdLabel(ach.kind, ach.threshold);
  const milestone = t(thr.key, thr.vars);
  const sub = t(`ach.sub.${ach.kind}`);

  // Log celebration view per achievement actually shown. Fires on the
  // first render (idx=0) and again each time the user advances to the
  // next one in a multi-milestone batch. Idempotent on DB side
  // (unique on user_id + achievement_id) so duplicate fires are no-ops.
  // Distinguishes "earned the badge" from "actually saw the celebration."
  useEffect(() => {
    if (ach?.id) {
      void logAchievementCelebrationView(ach.id);
    }
  }, [ach?.id]);

  async function finish() {
    const ids = achievements.map((a) => a.id);
    await markAchievementsRead(ids);
    onClose();
  }

  async function handleContinue() {
    if (isLast) {
      await finish();
    } else {
      setIdx(idx + 1);
    }
  }

  function handlePrev() {
    if (idx > 0) setIdx(idx - 1);
  }

  function jumpTo(targetIdx: number) {
    if (targetIdx >= 0 && targetIdx < total) setIdx(targetIdx);
  }

  async function handleShare() {
    const title = t('ach.celebrate.shareTitle', { milestone });
    const body = t('ach.celebrate.shareBody', { milestone, sub });

    // Render the badge to a PNG and use the Web Share API's files variant so
    // shares to iMessage/Instagram/etc. carry the actual badge image rather
    // than just text. Falls back to text-share, then to clipboard, so the
    // share button always does *something*.
    let file: File | null = null;
    try {
      const blob = await renderBadgeImage({
        kind: ach.kind,
        threshold: ach.threshold,
        milestone,
        sub,
        gradient: catalog.gradient,
      });
      file = new File([blob], `busy-${ach.kind}-${ach.threshold}.png`, { type: 'image/png' });
    } catch {
      file = null;
    }

    const nav = typeof navigator !== 'undefined' ? (navigator as Navigator) : null;
    if (nav?.share) {
      try {
        const payload: ShareData = { title, text: body };
        if (file && nav.canShare && nav.canShare({ files: [file] })) {
          payload.files = [file];
        }
        await nav.share(payload);
        return;
      } catch {
        // user cancelled or share failed — fall through to clipboard
      }
    }
    try {
      await navigator.clipboard.writeText(`${title}\n${body}`);
      toast(t('ach.celebrate.copied'), 'success');
    } catch {
      // no clipboard either — silent
    }
  }

  return (
    <ModalShell open onClose={finish} size="md" ariaLabel={milestone}>
      {/* Kind-aware celebration overlay — fire for streak, confetti for
          clients, fireworks for cuts, jackpot rain for revenue, lightning
          for lessons. Keyed on the current achievement so advancing to the
          next one in a multi-unlock fires a fresh effect. Intensity (1-8)
          scales with the threshold's tier index — earning the first
          milestone shows a quick spark, the eighth a sustained storm. */}
      <BadgeEffect
        key={ach.id}
        kind={ach.kind}
        intensity={intensityForThreshold(ach.kind, ach.threshold)}
      />

      {/* Hero — when the unlocked badge has custom artwork, the artwork takes
          over the hero with text overlaid via a bottom gradient; otherwise
          the gradient + KindIcon fallback keeps the unlock moment intact for
          badges still pending art. */}
      {(() => {
        const artwork = badgeArtworkUrl(ach.kind, ach.threshold);
        if (artwork) {
          return (
            <div className="relative overflow-hidden rounded-t-2xl bg-navy-900">
              <img
                src={artwork}
                alt=""
                className="w-full aspect-square object-cover"
                loading="eager"
              />
              <div className="absolute inset-x-0 bottom-0 px-7 pt-16 pb-7 text-center bg-gradient-to-t from-black/90 via-black/65 to-transparent text-white">
                <div className="text-[11px] tracking-[0.18em] uppercase font-extrabold text-gold-400 mb-2">
                  {t('ach.celebrate.eyebrow')}
                </div>
                <h2 className="font-display text-[32px] md:text-[40px] leading-[1.05] mb-1.5 drop-shadow">
                  {milestone}
                </h2>
                <p className="text-white/85 text-sm max-w-xs mx-auto leading-relaxed">{sub}</p>

                {total > 1 && (
                  <div className="mt-4 flex items-center justify-center gap-1.5">
                    {achievements.map((_, i) => (
                      <button
                        key={i}
                        type="button"
                        onClick={() => jumpTo(i)}
                        aria-label={t('ach.celebrate.jumpTo', { n: i + 1 })}
                        className={`w-2 h-2 rounded-full transition-colors hover:scale-125 ${
                          i === idx ? 'bg-white' : 'bg-white/30 hover:bg-white/60'
                        }`}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          );
        }
        return (
          <div className={`${catalog.gradient} text-white relative overflow-hidden rounded-t-2xl`}>
            <ConfettiOverlay />
            <div className="relative z-[1] px-7 pt-12 pb-9 text-center">
              <div className="text-[11px] tracking-[0.18em] uppercase font-extrabold text-white/80 mb-3">
                {t('ach.celebrate.eyebrow')}
              </div>
              <div className="mx-auto w-24 h-24 rounded-3xl bg-white/15 backdrop-blur-sm grid place-items-center mb-5 border border-white/20 shadow-lg">
                <KindIcon kind={ach.kind} className={`w-12 h-12 ${catalog.iconColor}`} />
              </div>
              <h2 className="font-display text-[36px] md:text-[44px] leading-[1.05] mb-2">
                {milestone}
              </h2>
              <p className="text-white/85 text-sm max-w-xs mx-auto leading-relaxed">{sub}</p>

              {total > 1 && (
                <div className="mt-5 flex items-center justify-center gap-1.5">
                  {achievements.map((_, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => jumpTo(i)}
                      aria-label={t('ach.celebrate.jumpTo', { n: i + 1 })}
                      className={`w-2 h-2 rounded-full transition-colors hover:scale-125 ${
                        i === idx ? 'bg-white' : 'bg-white/30 hover:bg-white/60'
                      }`}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* Action bar */}
      <div className="bg-bg-alt border-t border-line p-4 flex gap-2">
        {total > 1 && idx > 0 && (
          <button
            type="button"
            onClick={handlePrev}
            aria-label={t('ach.celebrate.prev')}
            className="h-11 px-3 rounded-lg border-2 border-line bg-surface text-ink-700 inline-flex items-center justify-center"
          >
            <ChevronLeftIcon className="w-4 h-4" />
          </button>
        )}
        <button
          type="button"
          onClick={handleShare}
          className="flex-1 h-11 rounded-lg border-2 border-navy-900 dark:border-gold-500 text-navy-900 dark:text-gold-500 font-semibold inline-flex items-center justify-center gap-2"
        >
          <ShareIcon className="w-4 h-4" />
          {t('ach.celebrate.share')}
        </button>
        <button
          type="button"
          onClick={handleContinue}
          className="flex-1 h-11 rounded-lg bg-navy-900 text-white dark:bg-gold-500 dark:text-navy-900 font-bold"
        >
          {isLast ? t('ach.celebrate.continue') : t('ach.celebrate.next')}
        </button>
      </div>
    </ModalShell>
  );
}

/* ============================ icons ============================ */

export function KindIcon({
  kind,
  className,
}: {
  kind: AchievementRow['kind'];
  className?: string;
}) {
  switch (kind) {
    case 'streak':
      return (
        <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
          <path d="M8.5 14.5A2.5 2.5 0 0 0 11 12c0-1.38-.5-2-1-3-1.072-2.143-.224-4.054 2-6 .5 2.5 2 4.9 4 6.5 2 1.6 3 3.5 3 5.5a7 7 0 1 1-14 0c0-1.153.433-2.294 1-3a2.5 2.5 0 0 0 2.5 2.5z" />
        </svg>
      );
    case 'clients':
      return (
        <svg
          className={className}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
      );
    case 'cuts':
      return (
        <svg
          className={className}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.25"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <circle cx="6" cy="6" r="3" />
          <circle cx="6" cy="18" r="3" />
          <line x1="20" y1="4" x2="8.12" y2="15.88" />
          <line x1="14.47" y1="14.48" x2="20" y2="20" />
          <line x1="8.12" y1="8.12" x2="12" y2="12" />
        </svg>
      );
    case 'revenue':
      return (
        <svg
          className={className}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.25"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <line x1="12" y1="1" x2="12" y2="23" />
          <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
        </svg>
      );
    case 'lessons':
      return (
        <svg
          className={className}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
          <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
        </svg>
      );
    case 'transformations':
      return (
        <svg
          className={className}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
          <circle cx="12" cy="13" r="4" />
        </svg>
      );
    case 'timekeeper':
      return (
        <svg
          className={className}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.25"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden
        >
          <circle cx="12" cy="12" r="10" />
          <polyline points="12 6 12 12 16 14" />
        </svg>
      );
  }
}

function ChevronLeftIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

function ShareIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.25"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
      <polyline points="16 6 12 2 8 6" />
      <line x1="12" y1="2" x2="12" y2="15" />
    </svg>
  );
}

// Subtle confetti dots to give the celebration moment some life without
// pulling in a heavy animation lib.
function ConfettiOverlay() {
  const dots = [
    { l: '8%',  t: '12%', size: 6,  delay: 0 },
    { l: '18%', t: '38%', size: 4,  delay: 0.6 },
    { l: '12%', t: '70%', size: 5,  delay: 0.3 },
    { l: '88%', t: '15%', size: 5,  delay: 0.4 },
    { l: '92%', t: '55%', size: 7,  delay: 0.1 },
    { l: '78%', t: '78%', size: 4,  delay: 0.7 },
    { l: '50%', t: '8%',  size: 4,  delay: 0.5 },
  ];
  return (
    <div aria-hidden className="absolute inset-0 pointer-events-none">
      {dots.map((d, i) => (
        <span
          key={i}
          className="absolute rounded-full bg-white/35 animate-[confetti_2.4s_ease-in-out_infinite]"
          style={{
            left: d.l,
            top: d.t,
            width: d.size,
            height: d.size,
            animationDelay: `${d.delay}s`,
          }}
        />
      ))}
      <style>{`
        @keyframes confetti {
          0%, 100% { transform: translateY(0) scale(1); opacity: .3; }
          50%      { transform: translateY(-8px) scale(1.15); opacity: .8; }
        }
      `}</style>
    </div>
  );
}
